import { THROW_R, type ThrowField } from "./diceThrow";
import { NARD_BAR, NARD_OFF, type NardLocal } from "./state";

/**
 * هندسه‌ی تخته‌ی تخته‌نرد به واحدِ تخته (y رو به پایین) — همان اعدادِ BackgammonView ِ اپ.
 *
 * خانه‌ی تو پایین-راست (خانه‌های ۱ تا ۶ ِ تو، ۱ کنارِ لبه)، بالا ۱۳ تا ۲۴ از چپ به راست،
 * بار وسط، سینیِ جمع سمتِ راست. هم تخته (BoardTable) و هم انیمیشنِ حرکت (controller) از
 * همین‌جا جای مهره‌ها را می‌گیرند تا پرواز درست سرِ جای کشیده‌شده بنشیند.
 */
export const NW = 17, NH = 12.4, NP = 0.3, NPW = 1.15, NBW = 0.9, NOW = 1.3;
export const NX0 = NP, NX1 = NX0 + 6 * NPW, NX2 = NX1 + NBW, NX3 = NX2 + 6 * NPW, NX4 = NX3 + 0.25;
export const NTOP = NP, NBOT = NH - NP, NTL = 4.9, NCHK = NPW * 0.9;
/** قطرِ مهره. */
export const NDIA = NCHK * 0.94;
/** بیشترینِ مهره‌ی کشیده در هر خانه (بیشتر = قرصِ شمار) و در بار. */
export const NSTACK = 5, NBARSTACK = 4;

/**
 * تاسِ پرتابی (diceThrow.ts، همان منچ و مارپله) روی تخته‌نرد: ضلعِ تاس به واحدِ تخته، و زمینِ پرتاب = ماهوتِ دو نیمه
 * با بارِ میانشان، بی سینیِ جمع. فیزیک به واحدِ پرتاب است (ضلعِ تاس ۲ × THROW_R) و NTK واحدِ تخته در هر واحدِ پرتاب.
 */
export const NDIE = 1.1;
const NTK = NDIE / (2 * THROW_R);
export const NARD_FIELD: ThrowField = { w: (NX3 - NX0) / NTK, h: (NBOT - NTOP) / NTK };
/** نقطه‌ای از زمینِ پرتاب روی تخته (واحدِ تخته). */
export const nardThrowXY = (x: number, y: number): [number, number] => [NX0 + x * NTK, NTOP + y * NTK];
/** پیکسلِ یک واحدِ پرتاب روی تخته‌ای به پهنای w پیکسل. */
export const nardThrowUnitPx = (w: number): number => (w / NW) * NTK;
/** گوشه‌ی صندلیِ محلیِ s (همان چیدمانِ گوشه‌های منچ): تو پایین-چپ (۰)، حریف روبه‌رو بالا-راست (۲) — همان marpeleCorner ِ دونفره. */
export const nardCorner = (s: number): number => (s === 0 ? 0 : 2);

/** ستونِ خانه‌ی محلیِ i: پایین از چپ ۱۱…۶ | بار | ۵…۰، بالا از چپ ۱۲…۱۷ | بار | ۱۸…۲۳. */
export function nardCol(i: number): { x: number; top: boolean; c: number } {
  const top = i >= 12;
  const c = top ? i - 12 : 11 - i;
  return { x: c < 6 ? NX0 + (c + 0.5) * NPW : NX2 + (c - 6 + 0.5) * NPW, top, c };
}

/** مرکزِ مهره‌ی k ام ِ خانه‌ی i، از لبه رو به وسط. */
export function nardChkXY(i: number, k: number): [number, number] {
  const { x, top } = nardCol(i);
  return [x, top ? NTOP + NCHK * (k + 0.5) : NBOT - NCHK * (k + 0.5)];
}

/** مرکزِ مهره‌ی k ام ِ بار: مالِ من زیرِ وسط، مالِ حریف بالای آن. */
export function nardBarXY(p: number, k: number): [number, number] {
  return [(NX1 + NX2) / 2, p === 0 ? NH / 2 + 0.85 + k * NCHK : NH / 2 - 0.85 - k * NCHK];
}

/** مرکزِ باریکه‌ی k ام در سینیِ جمع: مالِ من از پایین بالا، مالِ حریف از بالا پایین. */
export function nardOffXY(p: number, k: number): [number, number] {
  return [NX4 + NOW / 2, p === 0 ? NBOT - 0.18 - (k + 1) * 0.34 + 0.15 : NTOP + 0.18 + k * 0.34 + 0.15];
}

/** مهره‌های p روی خانه‌ی i (۰ اگر مالِ دیگری است). */
export function nardOwn(m: Pick<NardLocal, "pts">, i: number, p: number): number {
  const v = m.pts[i] ?? 0;
  return p === 0 ? Math.max(0, v) : Math.max(0, -v);
}

/** جای مهره‌ی بالاییِ p در جای i (خانه یا بار) — مبدأِ پرواز. */
export function nardTopXY(m: Pick<NardLocal, "pts" | "bar">, i: number, p: number): [number, number] {
  if (i === NARD_BAR) return nardBarXY(p, Math.max(0, Math.min(m.bar[p as 0 | 1] ?? 1, NBARSTACK) - 1));
  return nardChkXY(i, Math.max(0, Math.min(nardOwn(m, i, p), NSTACK) - 1));
}

/** جای فرودِ مهره‌ی p در i: روی ستونِ خودش، جای تک‌مهره‌ی زده‌شده، یا سینیِ جمع. */
export function nardLandXY(m: Pick<NardLocal, "pts" | "bar" | "off">, i: number, p: number): [number, number] {
  if (i === NARD_OFF) return nardOffXY(p, m.off[p as 0 | 1] ?? 0);
  if (i === NARD_BAR) return nardBarXY(p, Math.min(m.bar[p as 0 | 1] ?? 0, NBARSTACK - 1));
  return nardChkXY(i, Math.min(nardOwn(m, i, p), NSTACK - 1));
}

/** جای زیرِ یک نقطه‌ی تخته: خانه، بار، سینیِ جمعِ من، یا null بیرونِ تخته — برای کشیدن. */
export function nardLocAt(bx: number, by: number): number | null {
  if (bx < 0 || bx > NW || by < 0 || by > NH) return null;
  if (bx >= NX1 && bx <= NX2) return NARD_BAR;
  if (bx >= NX4 - 0.1) return by >= NH / 2 ? NARD_OFF : null;
  const c = bx < NX1 ? Math.min(5, Math.max(0, Math.floor((bx - NX0) / NPW))) : 6 + Math.min(5, Math.max(0, Math.floor((bx - NX2) / NPW)));
  return by < NH / 2 ? 12 + c : 11 - c;
}

/** ترتیبِ دیداریِ هر ردیف از چپ به راست — برای «لمسِ کنارِ مقصد»، مثلِ Snap ِ اپ. */
const ROW_BOTTOM = [11, 10, 9, 8, 7, 6, NARD_BAR, 5, 4, 3, 2, 1, 0, NARD_OFF];
const ROW_TOP = [12, 13, 14, 15, 16, 17, NARD_BAR, 18, 19, 20, 21, 22, 23];

/** اگر x خودش مجاز نیست ولی درست یک همسایه‌ی دیداری‌اش مجاز است، همان همسایه؛ وگرنه x. */
export function nardSnap(x: number, ok: readonly number[]): number {
  if (ok.includes(x)) return x;
  let hit: number | null = null;
  for (const row of [ROW_BOTTOM, ROW_TOP]) {
    const k = row.indexOf(x);
    if (k < 0) continue;
    for (const j of [k - 1, k + 1]) {
      const n = row[j];
      if (n === undefined || !ok.includes(n)) continue;
      if (hit !== null && hit !== n) return x;
      hit = n;
    }
  }
  return hit ?? x;
}
